import { RouletteSpinError } from './roulette-api';
import { mapPopup } from './roulette.mappers';
import type { ApiPostRouletteSpinPort } from '~/handlers/ports/api-post-roulette-spin.port';
import type { SpinResult } from '~/types/roulette';
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';

export class ApostatudoApiPostRouletteSpinAdapter
  implements ApiPostRouletteSpinPort
{
  async handle(slug: string, userId: number): Promise<SpinResult> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    let response: Response;

    try {
      response = await fetch(
        `${getApostatudoApiUrl()}/roulettes/${encodeURIComponent(slug)}/spin`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_id: userId }),
          signal: controller.signal,
        },
      );
    } catch {
      // Timeout (AbortError) ou queda de rede (TypeError).
      throw new RouletteSpinError('network');
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      if (response.status === 429) {
        throw new RouletteSpinError('rate_limited');
      }

      const err = await response.json().catch(() => ({}));

      // 502 delivery_failed: crédito falhou e o giro foi estornado — o usuário
      // pode girar de novo na hora. Kind próprio pra UI mostrar a mensagem sem
      // animar/abrir popup de prêmio.
      if (response.status === 502 || err?.error === 'delivery_failed') {
        throw new RouletteSpinError('delivery_failed', err?.message);
      }

      throw new RouletteSpinError('api', err?.message);
    }

    const json = await response.json();

    return {
      result: json.result,
      // Entrega síncrona e terminal: em vitória vem "delivered"; na derrota o back
      // manda "not_applicable" (absorvido em null — badge só existe em vitória).
      deliveryStatus: json.result === 'win' ? json.delivery_status : null,
      spinsRemaining: json.spins_remaining ?? 0,
      nextSpinAvailableAt: json.next_spin_available_at ?? null,
      // Destino canônico da roda. Sem position utilizável, vira null (a UI cai na
      // derivação pela id do prêmio) — nunca chuta a fatia 0, que é real.
      slice:
        json.slice && json.slice.position != null
          ? { id: json.slice.id, position: json.slice.position }
          : null,
      prize: json.prize
        ? {
            id: json.prize.id,
            // Sem fallback pra 0: 0 é uma fatia real e chutá-la mostraria prêmio
            // errado. null quando ausente; a UI resolve pela id (ver RouletteGame).
            position: json.prize.position ?? null,
            prizeName: json.prize.prize_name ?? '',
            popup: mapPopup(json.prize.popup),
          }
        : null,
      losePopup: mapPopup(json.lose_popup),
    };
  }
}
